import {
  createCamelCasedClient,
  createTokenRefreshInterceptor,
  getAffiliateLinkMetrics,
  getFinancePerformance,
} from '@metapic-apps/shared'
import axios, { type AxiosError, type AxiosInstance } from 'axios'

import { config } from '@/lib/helpers'
import { observeAdBlockerFailure } from '@/lib/services/ad-blocker.service'
import { notifyUserBanned } from '@/lib/services/banned-user.service'
import { useAuthStore } from '@/stores/auth.stores'

const financeAxios = axios.create({
  baseURL: config.financeApiUrl,
  // Serialize array query params as repeated keys (`metrics=a&metrics=b`) instead of
  // axios' default bracket form (`metrics[]=a`), which the finance API does not parse.
  paramsSerializer: { indexes: null },
})

if (import.meta.env.VITE_DEMO_MODE === 'true') {
  const { createDemoAdapter } = await import('@/demo/demo-adapter')
  financeAxios.defaults.adapter = createDemoAdapter()
}

financeAxios.interceptors.request.use(
  (request) => {
    const authStore = useAuthStore()
    request.headers.setAuthorization(authStore.authorization || '')
    request.headers.setContentType(request.isFormData ? 'multipart/form-data' : 'application/json')
    return request
  },
  (error: AxiosError) => Promise.reject(error),
)

financeAxios.interceptors.response.use(
  (response) => response,
  (
    error: AxiosError<{
      code?: string
      message?: string
    }>,
  ) => {
    if (error.response?.status === 403 && error.response?.data?.code === 'USER_BANNED') {
      notifyUserBanned()
    }
    void observeAdBlockerFailure(error)
    return Promise.reject(error)
  },
)

createTokenRefreshInterceptor(financeAxios, {
  refreshToken: () => useAuthStore().refreshToken(),
  onRefreshFailure: () => useAuthStore().handleRefreshFailure(),
})

const financeClientFactory = (axiosInstance: AxiosInstance) => ({
  ...getFinancePerformance(axiosInstance),
  ...getAffiliateLinkMetrics(axiosInstance),
})

export const financeClient = createCamelCasedClient(financeClientFactory, financeAxios)
